import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, collectionGroup, query, where, getDocs, doc, getDoc, orderBy, Timestamp, limit } from 'firebase/firestore';
import { CalendarIcon, ClockIcon, VideoIcon, MapPinIcon, LoaderIcon } from 'lucide-react';
import { selfLinkStudentToTeachers } from '../../services/studentService';
interface UpcomingLesson {
  id: string;
  teacherId: string;
  teacherName: string;
  teacherAvatar: string;
  instrument: string;
  date: string;
  time: string;
  duration: string;
  format: 'Virtual' | 'In-person';
  status: string;
  meetingLink?: string;
  isNext: boolean;
}
export const StudentDashboard = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser
  } = useAuth();
  const [studentName, setStudentName] = useState('');
  const [upcomingLessons, setUpcomingLessons] = useState<UpcomingLesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!currentUser?.uid) return;
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        console.log('🔍 [StudentDashboard] ===== DIAGNOSIS START =====');
        console.log('🔍 [StudentDashboard] currentUser.uid:', currentUser.uid);
        console.log('🔍 [StudentDashboard] currentUser.email:', currentUser.email);
        // Step 1: Get student's first name from users/{uid}
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        console.log('🔍 [StudentDashboard] users/{uid} doc exists:', userDoc.exists());
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('🔍 [StudentDashboard] userData.email:', userData.email);
          console.log('🔍 [StudentDashboard] userData.firstName:', userData.firstName);
          setStudentName(userData.firstName || userData.name || 'Student');
          // Step 1b: Self-link
          const studentEmail = userData.email || currentUser.email;
          console.log('🔍 [StudentDashboard] Email used for self-link:', studentEmail);
          if (studentEmail) {
            try {
              const linkCount = await selfLinkStudentToTeachers(studentEmail, currentUser.uid);
              console.log('🔍 [StudentDashboard] selfLinkStudentToTeachers result:', linkCount, 'new links created');
            } catch (linkError) {
              console.error('❌ [StudentDashboard] selfLinkStudentToTeachers FAILED:', linkError);
            }
          } else {
            console.warn('⚠️ [StudentDashboard] No email found — cannot self-link');
          }
        } else {
          console.warn('⚠️ [StudentDashboard] No users/{uid} document found for:', currentUser.uid);
        }
        // Step 2: Find all teacher-student links via collectionGroup('students')
        console.log('🔍 [StudentDashboard] Querying collectionGroup("students") where linkedUid ==', currentUser.uid);
        const studentsQuery = query(collectionGroup(db, 'students'), where('linkedUid', '==', currentUser.uid));
        const studentsSnap = await getDocs(studentsQuery);
        console.log('🔍 [StudentDashboard] linkedUid query results:', studentsSnap.size, 'docs');
        if (studentsSnap.empty) {
          console.warn('⚠️ [StudentDashboard] No linked teacher records found. Possible causes:');
          console.warn('   1. selfLinkStudentToTeachers did not find email match');
          console.warn('   2. Firestore rules/indexes not deployed');
          console.warn('   3. Teacher stored a different email for this student');
          setUpcomingLessons([]);
          setIsLoading(false);
          console.log('🔍 [StudentDashboard] ===== DIAGNOSIS END (no links) =====');
          return;
        }
        // Build a map of { studentId → teacherId }
        const studentTeacherPairs: {
          studentId: string;
          teacherId: string;
        }[] = [];
        studentsSnap.docs.forEach(docSnap => {
          const pathSegments = docSnap.ref.path.split('/');
          const teacherId = pathSegments[1];
          console.log('🔍 [StudentDashboard] Found link:', {
            studentDocId: docSnap.id,
            teacherId,
            path: docSnap.ref.path,
            data: {
              email: docSnap.data().email,
              linkedUid: docSnap.data().linkedUid
            }
          });
          studentTeacherPairs.push({
            studentId: docSnap.id,
            teacherId
          });
        });
        // Step 3: Query upcoming lessons from collectionGroup('calendar')
        // for each studentId, filtered to future lessons only
        const now = new Date();
        const allLessons: any[] = [];
        for (const pair of studentTeacherPairs) {
          const calendarQuery = query(collectionGroup(db, 'calendar'), where('studentId', '==', pair.studentId), where('startAt', '>=', Timestamp.fromDate(now)), orderBy('startAt', 'asc'), limit(10));
          const calendarSnap = await getDocs(calendarQuery);
          calendarSnap.docs.forEach(lessonDoc => {
            const data = lessonDoc.data();
            // Skip cancelled lessons
            if (data.status === 'CANCELED' || data.cancelled === true) {
              return;
            }
            allLessons.push({
              id: lessonDoc.id,
              teacherId: pair.teacherId,
              ...data
            });
          });
        }
        // Sort all lessons by startAt and take the first 3
        allLessons.sort((a, b) => {
          const aTime = a.startAt?.toDate ? a.startAt.toDate().getTime() : new Date(a.startAt).getTime();
          const bTime = b.startAt?.toDate ? b.startAt.toDate().getTime() : new Date(b.startAt).getTime();
          return aTime - bTime;
        });
        const topLessons = allLessons.slice(0, 3);
        // Step 4: Resolve teacher names from publicTeacherProfiles
        const teacherIds = [...new Set(topLessons.map(l => l.teacherId))];
        const teacherNameMap: Record<string, string> = {};
        for (const tid of teacherIds) {
          try {
            const profileDoc = await getDoc(doc(db, 'publicTeacherProfiles', tid));
            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              teacherNameMap[tid] = profileData.fullName || `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || 'Teacher';
            } else {
              teacherNameMap[tid] = 'Teacher';
            }
          } catch {
            teacherNameMap[tid] = 'Teacher';
          }
        }
        // Step 5: Transform into display format
        const formatted: UpcomingLesson[] = topLessons.map((lesson, index) => {
          const startDate = lesson.startAt?.toDate ? lesson.startAt.toDate() : new Date(lesson.startAt);
          const teacherFullName = teacherNameMap[lesson.teacherId] || 'Teacher';
          const initials = teacherFullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
          // Format date
          const today = new Date();
          const tomorrow = new Date();
          tomorrow.setDate(today.getDate() + 1);
          let dateStr: string;
          if (startDate.toDateString() === today.toDateString()) {
            dateStr = 'Today';
          } else if (startDate.toDateString() === tomorrow.toDateString()) {
            dateStr = 'Tomorrow';
          } else {
            dateStr = startDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            });
          }
          // Format time
          const timeStr = startDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          // Duration
          const durationMin = lesson.durationMinutes || 60;
          const durationStr = `${durationMin} min`;
          // Location type
          const location = (lesson.location || '').toLowerCase();
          const isOnline = location.includes('online') || location.includes('virtual') || location.includes('zoom') || location.includes('meet') || !!lesson.meetingLink;
          const format: 'Virtual' | 'In-person' = isOnline ? 'Virtual' : 'In-person';
          // Status display
          const rawStatus = (lesson.status || 'SCHEDULED').toUpperCase();
          let statusDisplay = 'Scheduled';
          if (rawStatus === 'SCHEDULED') statusDisplay = 'Confirmed';else if (rawStatus === 'COMPLETED') statusDisplay = 'Completed';
          return {
            id: lesson.id,
            teacherId: lesson.teacherId,
            teacherName: teacherFullName,
            teacherAvatar: initials,
            instrument: lesson.instrument || 'Music',
            date: dateStr,
            time: timeStr,
            duration: durationStr,
            format,
            status: statusDisplay,
            meetingLink: lesson.meetingLink,
            isNext: index === 0
          };
        });
        setUpcomingLessons(formatted);
      } catch (error) {
        console.error('Error fetching student dashboard data:', error);
        setUpcomingLessons([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboardData();
  }, [currentUser?.uid]);
  return <div className="min-h-screen p-6 md:p-8" data-id="element-6889">
      <div className="max-w-4xl mx-auto space-y-8" data-id="element-6890">
        {/* Welcome Header */}
        <div data-id="element-6891">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6892">
            Welcome back{studentName ? `, ${studentName}` : ''}
          </h1>
          <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6893">
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>

        {/* Upcoming Lessons Section */}
        <div data-id="element-6894">
          <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6895">
            Upcoming Lessons
          </h2>

          {isLoading ? <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`} data-id="element-6896">
              <LoaderIcon className={`w-8 h-8 animate-spin mx-auto mb-3 ${isDark ? 'text-green-400' : 'text-green-500'}`} data-id="element-6897" />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6898">
                Loading your lessons...
              </p>
            </div> : upcomingLessons.length > 0 ? <div className="space-y-3" data-id="element-6899">
              {upcomingLessons.map(lesson => <div key={lesson.id} className={`rounded-lg p-5 shadow-sm border transition-all ${lesson.isNext ? isDark ? 'bg-navy-800 border-blue-500/30 shadow-blue-500/10' : 'bg-white border-blue-200 shadow-blue-100' : isDark ? 'bg-navy-800 border-navy-700 hover:bg-navy-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-id="element-6900">
                  <div className="flex items-center justify-between gap-4" data-id="element-6901">
                    <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-6902">
                      {/* Teacher Avatar */}
                      <div className={`${lesson.isNext ? 'w-12 h-12' : 'w-10 h-10'} rounded-full flex items-center justify-center ${lesson.isNext ? 'text-base' : 'text-sm'} font-semibold flex-shrink-0 ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`} data-id="element-6903">
                        {lesson.teacherAvatar}
                      </div>

                      {/* Lesson info */}
                      <div className="flex-1 min-w-0" data-id="element-6904">
                        <div className="flex items-center gap-2 mb-1" data-id="element-6905">
                          <h3 className={`${lesson.isNext ? 'text-base' : 'text-sm'} font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6906">
                            {lesson.instrument}
                          </h3>
                          {lesson.isNext && <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`} data-id="element-6907">
                              {lesson.status}
                            </span>}
                          <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-navy-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-6908">
                            {lesson.duration}
                          </span>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`} data-id="element-6909">
                          with {lesson.teacherName}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-sm" data-id="element-6910">
                          <div className="flex items-center gap-1.5" data-id="element-6911">
                            <ClockIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6912" />
                            <span className={isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6913">
                              {lesson.date} at {lesson.time}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5" data-id="element-6914">
                            {lesson.format === 'Virtual' ? <VideoIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6915" /> : <MapPinIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6916" />}
                            <span className={isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6917">
                              {lesson.format}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 flex-shrink-0" data-id="element-6918">
                      {lesson.isNext && lesson.format === 'Virtual' ? <>
                          <button onClick={() => {
                    if (lesson.meetingLink) {
                      window.open(lesson.meetingLink, '_blank');
                    }
                  }} className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-6919">
                            Join Lesson
                          </button>
                          <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-6920">
                            View Details
                          </button>
                        </> : <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-6921">
                          View Details
                        </button>}
                    </div>
                  </div>
                </div>)}
            </div> :
        // Empty state
        <div className={`rounded-xl p-12 text-center border-2 border-dashed ${isDark ? 'bg-navy-800/50 border-navy-700' : 'bg-gray-50 border-gray-300'}`} data-id="element-6922">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-6923">
                <CalendarIcon className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6924" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6925">
                No upcoming lessons yet
              </h3>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6926">
                Your upcoming lessons will appear here.
              </p>
              <button onClick={() => navigate('/find-teachers')} className="px-6 py-2.5 bg-deep-pink hover:bg-burgundy text-white rounded-lg font-medium transition-colors" data-id="element-6927">
                Find a Teacher
              </button>
            </div>}
        </div>
      </div>
    </div>;
};